import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '@/shared/lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap border text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-celestial-light/70 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'md',
      variant: 'primary',
    },
    variants: {
      size: {
        lg: 'h-11 px-5',
        md: 'h-10 px-4',
        sm: 'h-9 px-3.5',
      },
      variant: {
        ghost:
          'rounded-full border-[var(--border)] bg-transparent text-arctic-mist shadow-[var(--shadow-subtle-border)] hover:bg-[rgba(186,215,247,0.04)]',
        primary: 'rounded-md border-transparent bg-neon-violet text-white hover:bg-neon-violet/90',
        secondary:
          'rounded-full border-white/40 bg-[rgba(186,214,247,0.06)] text-white shadow-[var(--shadow-subtle-border)] hover:bg-[rgba(186,214,247,0.12)]',
      },
    },
  },
)

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return <Comp ref={ref} className={cn(buttonVariants({ size, variant }), className)} {...props} />
  },
)

Button.displayName = 'Button'
